import React, { useState, useEffect, useMemo, useRef } from "react";
import { commaView } from "../utils/number";
import { buildStops, nearestIndex } from "../utils/sliderStops";
import "../styles/RangeSlider.css";

// ============================================================
// 두 손잡이 범위 슬라이더 + 숫자 입력칸
//
// 동작 규칙 (네이버 부동산 방식):
//   - 슬라이더를 끄는 동안 → 숫자칸만 따라 바뀌고 검색은 하지 않음
//   - 손을 뗄 때          → 그때 한 번만 부모에게 알림(=검색 실행)
//   - 숫자칸에 직접 입력   → 그 값 그대로 검색, 손잡이는 가장 가까운 칸으로 이동
//   - 양끝에 붙어 있으면   → "조건 없음"으로 보고 빈 값("")을 넘김
//
// 연관 파일:
//   - utils/sliderStops.js               : 칸(정류장) 배열 계산
//   - components/SliderConfigManager.jsx : 관리자가 범위/단위를 정하는 화면
//   - styles/RangeSlider.css             : 겹친 슬라이더 손잡이 스타일
//   - App.js 필터 패널                    : 이 부품을 배치하고 setFilters 로 연결
//
// ⚠ 주의: config 는 매 렌더마다 새로 만들면 안 된다.
//    새 객체가 오면 칸 배열이 다시 계산되고 손잡이가 초기화된다.
//    App.js 에서 state 에 담아둔 값을 그대로 넘길 것.
// ============================================================

export default function RangeSlider({
  config, minValue, maxValue, onCommit, unit = "", skipZero = false,
}) {
  // 설정이 바뀔 때만 칸 배열을 다시 만든다 (매 렌더 재계산 방지)
  // [SKIP_ZERO_V1] skipZero=true 면 0 을 뺀 배열이 만들어진다 (예: 해당층)
  const stops = useMemo(() => buildStops(config, skipZero), [config, skipZero]);
  const last = stops.length - 1;

  // 손잡이 위치. 값이 아니라 '몇 번째 칸인가'로 다룬다.
  const [lo, setLo] = useState(0);
  const [hi, setHi] = useState(last);

  // 숫자 입력칸의 글자. 슬라이더와 별도로 둬야 타이핑이 방해받지 않는다.
  const [loText, setLoText] = useState("");
  const [hiText, setHiText] = useState("");

  // 드래그 중 표시. 드래그 중에는 외부 값으로 덮어쓰지 않는다.
  const dragging = useRef(false);

  // ── 바깥(filters)에서 값이 바뀌면 화면에 반영 ───────────
  // 예: '초기화' 버튼을 눌렀을 때. 드래그 중에는 건드리지 않는다.
  useEffect(() => {
    if (dragging.current) return;
    const hasLo = minValue !== "" && minValue !== null && minValue !== undefined;
    const hasHi = maxValue !== "" && maxValue !== null && maxValue !== undefined;
    setLo(hasLo ? nearestIndex(stops, minValue) : 0);
    setHi(hasHi ? nearestIndex(stops, maxValue) : last);
    setLoText(hasLo ? String(minValue) : "");
    setHiText(hasHi ? String(maxValue) : "");
  }, [minValue, maxValue, stops, last]);

  // ── 확정(부모에게 알림) ────────────────────────────────
  // 양끝에 붙어 있으면 "" 를 넘겨 조건에서 제외시킨다.
  // 이렇게 해야 값이 비어있는(NULL) 매물이 통째로 걸러지지 않는다.
  const commitByIndex = (a, b) => {
    onCommit(a === 0 ? "" : stops[a], b === last ? "" : stops[b]);
  };

  // ── 슬라이더 조작 ──────────────────────────────────────
  const onLoChange = (e) => {
    dragging.current = true;
    const v = Math.min(Number(e.target.value), hi); // 최대 손잡이를 넘지 못하게
    setLo(v);
    setLoText(v === 0 ? "" : String(stops[v]));     // 숫자칸도 즉시 따라감
  };
  const onHiChange = (e) => {
    dragging.current = true;
    const v = Math.max(Number(e.target.value), lo);
    setHi(v);
    setHiText(v === last ? "" : String(stops[v]));
  };
  // 손을 뗄 때 딱 한 번만 검색 실행 (드래그 중 서버 요청 폭주 방지)
  const onRelease = () => {
    dragging.current = false;
    commitByIndex(lo, hi);
  };

  // ── 숫자 직접 입력 ─────────────────────────────────────
  // 타이핑 중에는 글자만 바꾸고, 칸을 벗어날 때(blur) 확정한다.
  const commitText = () => {
    const rawLo = String(loText).replace(/,/g, "").trim();
    const rawHi = String(hiText).replace(/,/g, "").trim();
    let a = rawLo === "" ? "" : Number(rawLo);
    let b = rawHi === "" ? "" : Number(rawHi);

    // 최소가 최대보다 크면 서로 바꿔준다 (실수 방지)
    if (a !== "" && b !== "" && a > b) { const t = a; a = b; b = t; }
    setLoText(a === "" ? "" : String(a));
    setHiText(b === "" ? "" : String(b));

    // 손잡이는 가장 가까운 칸으로 이동 (표시용)
    setLo(a === "" ? 0    : nearestIndex(stops, a));
    setHi(b === "" ? last : nearestIndex(stops, b));

    // 검색에는 입력한 값을 '그대로' 쓴다 (칸에 맞춰 반올림하지 않음)
    onCommit(a, b);
  };

  // ── 화면 ──────────────────────────────────────────────
  const pct = (i) => (last <= 0 ? 0 : (i / last) * 100);

  const numStyle = {
    width: "100%", height: "26px", padding: "0 4px",
    border: "1px solid #cbd5e1", borderRadius: "4px",
    fontSize: "12px", textAlign: "center", minWidth: 0,
  };

  return (
    <div style={{ padding: "2px 0 0" }}>
      {/* 슬라이더: input[range] 두 개를 겹쳐 쌓는다.
          막대는 아래 div 들이 그리고, range 입력은 손잡이만 보이게 한다. */}
      <div style={{ position: "relative", height: "26px", margin: "0 5px" }}>
        <div style={{
          position: "absolute", top: "11px", left: 0, right: 0, height: "4px",
          background: "#e2e8f0", borderRadius: "2px",
        }} />
        <div style={{
          position: "absolute", top: "11px", height: "4px",
          left: `${pct(lo)}%`, width: `${pct(hi) - pct(lo)}%`,
          background: "#3b82f6", borderRadius: "2px",
        }} />

        <input
          type="range" min={0} max={last} step={1} value={lo}
          onChange={onLoChange}
          onMouseUp={onRelease} onTouchEnd={onRelease} onKeyUp={onRelease}
          className="rs-thumb"
          style={{ position: "absolute", top: 0, left: 0, width: "100%",
                   zIndex: lo > last - 2 ? 5 : 3 }}
        />
        <input
          type="range" min={0} max={last} step={1} value={hi}
          onChange={onHiChange}
          onMouseUp={onRelease} onTouchEnd={onRelease} onKeyUp={onRelease}
          className="rs-thumb"
          style={{ position: "absolute", top: 0, left: 0, width: "100%", zIndex: 4 }}
        />
      </div>

      {/* 숫자 입력칸 */}
      <div style={{ display: "flex", alignItems: "center", gap: "4px", marginTop: "3px" }}>
        <div style={{ flex: "1 1 0", minWidth: 0 }}>
          <input
            type="text" inputMode="decimal" style={numStyle} placeholder="최소" value={commaView(loText)}
            onChange={(e) => setLoText(String(e.target.value).replace(/,/g, ""))}
            onBlur={commitText}
            onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
          />
        </div>
        <span style={{ fontSize: "11px", color: "#94a3b8", flexShrink: 0 }}>~</span>
        <div style={{ flex: "1 1 0", minWidth: 0 }}>
          <input
            type="text" inputMode="decimal" style={numStyle} placeholder="최대" value={commaView(hiText)}
            onChange={(e) => setHiText(String(e.target.value).replace(/,/g, ""))}
            onBlur={commitText}
            onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
          />
        </div>
        {unit && <span style={{ fontSize: "11px", color: "#94a3b8", flexShrink: 0 }}>{unit}</span>}
      </div>

      {/* [SUMMARY_OFF_V1] 요약 줄 제거 — 숫자칸에 값이 보이므로 중복 */}
    </div>
  );
}
